import React, { useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, ImageIcon } from "@/components/icons";
import type { IdeaResourceLocalMediaItem } from "@/domains/Ideas/types";

interface InlineMediaPlayerProps {
  items: IdeaResourceLocalMediaItem[];
  // Locks the player frame to the first item's natural aspect ratio
  // (default). Set false to let each item size organically — useful for
  // mixed-aspect carousels where the user expects letterboxing.
  lockAspectToFirst?: boolean;
}

export const InlineMediaPlayer: React.FC<InlineMediaPlayerProps> = ({
  items,
  lockAspectToFirst = true,
}) => {
  const [index, setIndex] = useState(0);

  if (!items.length) return null;

  const aspect = lockAspectToFirst ? aspectFor(items[0]) : aspectFor(items[index]);
  const isCarousel = items.length > 1;
  const active = items[index];

  return (
    <div className="rounded-xl border border-gray-200 bg-black overflow-hidden mx-auto w-full max-w-[375px]">
      <div
        className="relative w-full bg-black max-h-[667px]"
        style={{ aspectRatio: aspect }}
      >
        <MediaSlide item={active} />
        {isCarousel && (
          <>
            <CarouselButton
              direction="prev"
              disabled={index === 0}
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
            />
            <CarouselButton
              direction="next"
              disabled={index === items.length - 1}
              onClick={() =>
                setIndex((i) => Math.min(items.length - 1, i + 1))
              }
            />
            <CarouselDots
              count={items.length}
              active={index}
              onSelect={setIndex}
            />
          </>
        )}
      </div>
    </div>
  );
};

const MediaSlide: React.FC<{ item: IdeaResourceLocalMediaItem }> = ({ item }) => {
  const [errored, setErrored] = useState(false);

  if (!item.url || errored) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
        <ImageIcon className="w-10 h-10 text-gray-500" />
      </div>
    );
  }

  if (item.mediaType.startsWith("video/")) {
    return (
      <video
        key={item.url}
        src={item.url}
        controls
        autoPlay
        muted
        playsInline
        loop
        preload="metadata"
        onError={() => setErrored(true)}
        className="absolute inset-0 w-full h-full object-contain bg-black"
      />
    );
  }

  return (
    <img
      key={item.url}
      src={item.url}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
      className="absolute inset-0 w-full h-full object-contain bg-black"
    />
  );
};

const CarouselButton: React.FC<{
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
}> = ({ direction, disabled, onClick }) => {
  const Icon = direction === "prev" ? ChevronLeftIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === "prev" ? "Previous" : "Next"}
      className={`absolute top-1/2 -translate-y-1/2 ${
        direction === "prev" ? "left-2" : "right-2"
      } w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition`}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
};

const CarouselDots: React.FC<{
  count: number;
  active: number;
  onSelect: (i: number) => void;
}> = ({ count, active, onSelect }) => (
  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
    {Array.from({ length: count }).map((_, i) => (
      <button
        key={i}
        type="button"
        onClick={() => onSelect(i)}
        aria-label={`Item ${i + 1} of ${count}`}
        className={`w-1.5 h-1.5 rounded-full transition ${
          i === active ? "bg-white" : "bg-white/40 hover:bg-white/60"
        }`}
      />
    ))}
  </div>
);

function aspectFor(item: IdeaResourceLocalMediaItem): string {
  if (item.width && item.height && item.width > 0 && item.height > 0) {
    return `${item.width} / ${item.height}`;
  }
  // Sensible default for the common reel / portrait carousel case.
  return "9 / 16";
}
